import type { ReactNode } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { UsageMeter } from "../usage/UsageMeter.tsx";
import { useWorkspace } from "./AppShell.tsx";
import { ModeSwitch } from "./ModeSwitch.tsx";
import { useSession } from "./session.tsx";
import { UserMenu } from "./UserMenu.tsx";
import type { WorkspaceMode } from "./workspace.ts";

interface Props {
  readonly mode: WorkspaceMode;
  /** Dónde estás: el título de la conversación, o la sección del panel. */
  readonly context?: ReactNode;
  /** Lo contextual: los ajustes en el chat, el mes en el panel. */
  readonly actions?: ReactNode;
  onMode: (mode: WorkspaceMode) => void;
}

/**
 * La cabecera, idéntica en los dos armazones: contexto a la izquierda,
 * conmutador de modo al centro, lo contextual a la derecha.
 *
 * El conmutador NO es condicional. Antes esta barra enseñaba tres pestañas de
 * contenido solo cuando no había conversación abierta, con el argumento de que
 * dentro de un chat manda el CONTEXTO --el título-- y enseñar las dos cosas
 * obligaba a mirar dos veces. Ese argumento valía para tres pestañas que
 * competían con el título por el mismo hueco; un conmutador de dos modos es
 * otra cosa: es el mueble fijo desde el que se cambia de sitio, y esconderlo
 * dejaba como única salida "Conversación nueva", que además destruye el turno.
 *
 * La rejilla de tres columnas con el centro al tamaño de su contenido es lo
 * que mantiene el conmutador centrado en la ventana aunque los lados pesen
 * distinto; centrarlo con `justify-between` lo haría bailar con cada título.
 */
export function TopBar({ mode, context, actions, onMode }: Props) {
  const { can, canSeeCosts } = useSession();
  const { chat } = useWorkspace();
  return (
    <header className="grid h-14 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b px-3">
      <div className="flex min-w-0 items-center gap-1">{context}</div>
      <ModeSwitch
        mode={mode}
        onMode={(next) => {
          if (next !== mode) {
            analytics.nav.modeSwitched({
              from: mode,
              to: next,
              turnBusy:
                chat.status === "submitted" || chat.status === "streaming",
              pendingDecisions: chat.pendingDecisions,
            });
          }
          onMode(next);
        }}
      />
      <div className="flex min-w-0 items-center justify-end gap-2">
        {/* El consumo lo pone la cabecera y no cada armazón: tiene que estar
            en los dos modos, siempre, y en el mismo sitio (2.4.3). */}
        {/* El contador pide el consumo de todo el programa: sin
            `usage.read` solo serían dos errores en la cabecera. */}
        {canSeeCosts && can(PERMISSIONS.usageRead) && <UsageMeter />}
        {actions}
        {/* Quién está dentro, al final: el borde de la ventana es donde se
            busca la cuenta, y así no se mueve al cambiar lo contextual. */}
        <UserMenu />
      </div>
    </header>
  );
}
